import { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { ClockCircleOutlined } from '@ant-design/icons';

import { LightTooltip } from '@components/LightTooltip';
import { dateFormatter } from '@utils';
import { adminHttp } from '@network';
import { Timer } from '@types';

interface Props {
  title?: string;
  url: string;
  minified?: boolean;
}

export const TimerWidget = ({ title, url, minified }: Props) => {
  const cls = useStyles();
  const [timer, setTimer] = useState<Timer>();

  useEffect(() => {
    (async () => {
      const nextTimer = await adminHttp.getTimer(url);
      setTimer(nextTimer);
    })();
  }, [url]);

  const { starts, ends, timerText } = timer || {};

  if (minified) {
    return <>{timerText || 'No data yet'}</>;
  }

  const Content = (
    <div className={cls.box}>
      {title && (
        <div className={cls.title}>{title}</div>
      )}
      <div className={cls.button}
        style={{ fontSize: `${timerText}`.length > 20 ? 14 : undefined }}>
        <ClockCircleOutlined /> {timerText || 'No data yet'}

        <LightTooltip extraWidth className={cls.tooltipInfo} title={(
          <div className={cls.tooltip}>
            <div className={cls.tooltipCol}>
              <div className={cls.tooltipTitle}>From &quot;{starts?.title}&ldquo;</div>
              <div>{starts?.date ? dateFormatter.toDateTime(starts?.date, true) : 'N/A'}</div>
            </div>
            <div className={cls.tooltipArrow}>→</div>
            <div className={cls.tooltipCol}>
              <div className={cls.tooltipTitle}>to &quot;{ends?.title}&ldquo;</div>
              <div>{ends?.date ? dateFormatter.toDateTime(ends?.date, true) : 'N/A'}</div>
            </div>
          </div>
        )}>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 0C4.48421 0 0 4.48421 0 10C0 15.5158 4.48421 20 10 20C15.5158 20 20 15.5158 20 10C20 4.48421 15.5158 0 10 0ZM11.8105 14.0421C11.6 14.5684 11.1158 15.4737 10.1474 16C9.55789 16.3158 9.13684 16.2947 9.03158 16.2947C8.90526 16.2947 8.06316 16.2526 7.70526 15.5789C7.53684 15.2211 7.57895 14.8632 7.6 14.7158L9.17895 8.90526H8.37895C8.27368 8.90526 8.18947 8.8421 8.16842 8.73684L8.12632 8.4C8.10526 8.29474 8.18947 8.18947 8.29474 8.16842L11.5579 7.62105C11.7053 7.6 11.8316 7.72632 11.7895 7.87368L10.0632 14.2947C10 14.5684 10.3368 14.8 10.5684 14.5895C10.8842 14.2947 11.0947 13.9789 11.2211 13.7263C11.2842 13.6211 11.4105 13.5789 11.5158 13.6421C11.6 13.6842 11.6632 13.7474 11.7474 13.7895C11.8316 13.8316 11.8526 13.9368 11.8105 14.0421ZM11.0105 6.52632C10.2316 6.52632 9.6 5.89474 9.6 5.11579C9.6 4.33684 10.2316 3.70526 11.0105 3.70526C11.7895 3.70526 12.4211 4.33684 12.4211 5.11579C12.4211 5.89474 11.7895 6.52632 11.0105 6.52632Z" fill="white"/>
          </svg>
        </LightTooltip>
      </div>
    </div>
  );

  if (starts || ends) {
    return (
      Content
    );
  }

  return Content;
};

const useStyles = makeStyles({
  tooltip: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  tooltipCol: {},
  tooltipTitle: {
    color: 'var(--tooltip-color)',
    fontStyle: 'italic',
  },
  tooltipArrow: {
    paddingLeft: 6,
    paddingRight: 8,
  },
  box: {
    fontFamily: 'var(--font-primary);',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',

    '& svg': {
      color: 'white',
      display: 'inline-block',
      marginRight: 8,
    },
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    paddingBottom: 5,
    lineHeight: 1.05,
    textAlign: 'center',
    color: 'var(--text-color)',
  },
  button: {
    background: 'var(--primary-color)',
    color: 'white',
    padding: '18px 30px',
    borderRadius: 8,
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    maxWidth: 300,
    whiteSpace: 'nowrap',
  },
  tooltipInfo: {
    textAlign: 'center',
    fontSize: 12,
    color: 'var(--primary-color)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
});
